import { cn } from '../lib/utils'
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    CardDescription,
} from '../Components/ui/card'
import { Input } from '../Components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../Components/ui/select'
import { Button } from '../Components/ui/button'
import { Label } from '../Components/ui/label'
import { AuthView, AcceptInvitationCard } from '@daveyplate/better-auth-ui'
import { useLocation } from 'wouter'
import { useState, useEffect, useMemo } from 'react'
import type React from 'react'
import { countries } from '../countries'
import { trpc } from '../system/trpc'
import { Toaster } from '../Components/ui/sonner'
import { toast } from 'sonner'
import SessionStore from '../State/SessionStore'
import { authClient } from '../lib/auth-client'

interface AuthPageProps {
    authView: string
}

export default function AuthPage({ authView }: AuthPageProps) {
    const [, setLocation] = useLocation()

    // Memoize URL params to prevent unnecessary re-renders
    const urlParams = useMemo(() => {
        const searchParams = new URLSearchParams(window.location.search)
        return {
            redirectTo: searchParams.get('redirectTo') || '/',
            invitationId: searchParams.get('invitationId'),
            email: searchParams.get('email'),
        }
    }, [])

    const redirectPath = urlParams.redirectTo

    // Memoize localization object to prevent unnecessary re-renders
    const localization = useMemo(
        () => ({
            NAME: 'Company Name',
            NAME_PLACEHOLDER: 'Company Name',
            SIGN_UP: 'Create an Account',
            SIGN_UP_DESCRIPTION:
                'Enter your information below to create an account for your company',
            MAGIC_LINK: 'Email Link',
            MAGIC_LINK_ACTION: 'Send Email Link',
        }),
        []
    )

    return (
        <main className="flex grow flex-col items-center justify-center gap-3 self-center p-4 md:p-6 bg-background w-full">
            <Toaster position="top-center" />
            <img
                src={
                    new URL('/public/coincraft_logo_130.png', import.meta.url)
                        .href
                }
                alt="Coincraft Logo"
                className="size-28 mb-10"
            />
            {authView === 'account-details' ? (
                <AccountDetails redirectPath={redirectPath} />
            ) : authView === 'accept-invitation' ? (
                <AcceptInvitationView redirectPath={redirectPath} />
            ) : authView === 'sign-out' ? (
                <SignOutView />
            ) : authView === 'verify' ? (
                <MagicLinkVerify redirectPath={redirectPath} />
            ) : (
                <AuthView
                    className="mb-28"
                    pathname={authView}
                    redirectTo={redirectPath}
                    callbackURL={redirectPath}
                    localization={localization}
                />
            )}
        </main>
    )
}

const AcceptInvitationView = ({
    redirectPath = '/select-organisation',
}: {
    redirectPath?: string
}) => {
    const [, setLocation] = useLocation()
    const searchParams = new URLSearchParams(window.location.search)
    const invitationId = searchParams.get('invitationId')
    const session = authClient.useSession()
    const [invitationDetails, setInvitationDetails] = useState<{
        email: string
        organisationId: string
        status: string
    } | null>(null)
    const [accountExists, setAccountExists] = useState<boolean | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const checkInvitation = async () => {
            if (!invitationId) {
                setIsLoading(false)
                return
            }

            try {
                // Get invitation details
                const details = await trpc.auth.getInvitationDetails.query({
                    invitationId,
                })
                setInvitationDetails(details)

                // Only check if account exists when user is not logged in
                if (!session.data?.user) {
                    const accountCheck =
                        await trpc.auth.checkAccountExists.query({
                            email: details.email,
                        })
                    setAccountExists(accountCheck.exists)
                }
            } catch (error) {
                console.error('Error checking invitation:', error)
            } finally {
                setIsLoading(false)
            }
        }

        checkInvitation()
    }, [invitationId, session.data?.user])

    // Handle navigation based on account existence
    useEffect(() => {
        // Only navigate when we have determined account existence
        if (accountExists === null || !invitationDetails || !invitationId) {
            return
        }

        if (accountExists) {
            // Redirect to sign-in with the invitation ID
            setLocation(
                `/auth/sign-in?invitationId=${invitationId}&redirectTo=/auth/accept-invitation`
            )
        } else {
            // Redirect to sign-up with prefilled email
            setLocation(
                `/auth/sign-up?invitationId=${invitationId}&email=${invitationDetails.email}&redirectTo=/auth/accept-invitation`
            )
        }
    }, [accountExists, invitationDetails, invitationId, setLocation])

    // If user is already logged in, show the invitation card
    if (session.data?.user) {
        if (!invitationId) {
            return (
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <CardTitle className="text-lg md:text-xl text-red-600">
                            Invalid Invitation
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            No invitation ID provided
                        </p>
                        <Button
                            onClick={() => setLocation('/auth/sign-in')}
                            className="w-full"
                        >
                            Go to Sign In
                        </Button>
                    </CardContent>
                </Card>
            )
        }

        return (
            <AcceptInvitationCard
                className="w-full max-w-md"
                invitationId={invitationId}
                onSuccess={async () => {
                    toast.success('Welcome to the team!')

                    // Set this organisation as active
                    if (invitationDetails?.organisationId) {
                        await authClient.organization.setActive({
                            organizationId: invitationDetails.organisationId,
                        })
                    }

                    // Reload to get fresh session with staff record
                    setTimeout(() => {
                        window.location.href = '/'
                    }, 500)
                }}
                onError={(error) => {
                    toast.error(error.message || 'Failed to accept invitation')
                }}
            />
        )
    }

    // User is not logged in - check if they have an account
    if (isLoading) {
        return (
            <Card className="w-full max-w-md">
                <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
                    <p className="text-center text-muted-foreground">
                        Checking invitation...
                    </p>
                </CardContent>
            </Card>
        )
    }

    if (!invitationId || !invitationDetails) {
        return (
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle className="text-lg md:text-xl text-red-600">
                        Invalid Invitation
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        {!invitationId
                            ? 'No invitation ID provided'
                            : 'Invitation not found or expired'}
                    </p>
                    <Button
                        onClick={() => setLocation('/auth/sign-in')}
                        className="w-full"
                    >
                        Go to Sign In
                    </Button>
                </CardContent>
            </Card>
        )
    }

    // Show loading state while navigation is happening
    return (
        <Card className="w-full max-w-md">
            <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
                <p className="text-center text-muted-foreground">
                    Redirecting...
                </p>
            </CardContent>
        </Card>
    )
}

const AccountDetails = ({
    redirectPath = '/select-organisation',
}: {
    redirectPath?: string
}) => {
    const [, setLocation] = useLocation()
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [countryCode, setCountryCode] = useState('')
    const [industry, setIndustry] = useState('')
    const [otherIndustry, setOtherIndustry] = useState('')
    const [companySize, setCompanySize] = useState('')
    const [referralSource, setReferralSource] = useState('')
    const [otherReferralSource, setOtherReferralSource] = useState('')
    const [termsAccepted, setTermsAccepted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errors, setErrors] = useState<Record<string, boolean>>({})

    useEffect(() => {
        fetch('https://cloudflare.com/cdn-cgi/trace')
            .then((response) => response.text())
            .then((data) => {
                const parsedData = Object.fromEntries(
                    data
                        .trim()
                        .split('\n')
                        .map((line) => line.split('='))
                )
                if (!countryCode && parsedData.loc) {
                    setCountryCode(parsedData.loc.toLowerCase())
                }
            })
            .catch(() => {
                // Silently fail if geolocation doesn't work
            })
    }, [])

    const validateForm = () => {
        const newErrors: Record<string, boolean> = {}

        if (!firstName.trim()) newErrors.firstName = true
        if (!lastName.trim()) newErrors.lastName = true
        if (!countryCode) newErrors.countryCode = true
        if (!termsAccepted) newErrors.termsAccepted = true

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            return
        }

        setIsSubmitting(true)
        setErrors({})

        try {
            const result = await trpc.auth.registerDetails.mutate({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                country: countryCode,
                phoneNumber: phoneNumber.trim() || undefined,
                industry:
                    industry !== 'other'
                        ? industry || undefined
                        : otherIndustry.trim() || undefined,
                companySize: companySize || undefined,
                referralSource:
                    referralSource !== 'other'
                        ? referralSource || undefined
                        : otherReferralSource.trim() || undefined,
            })

            if (result.success) {
                // Navigate to organisation selection to choose/create org
                window.location.href = redirectPath
            } else {
                throw new Error('Failed to update account details')
            }
        } catch (error) {
            console.error(
                '[AccountDetails] Failed to save account details:',
                error
            )
            alert('Failed to save account details. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Card className="w-full max-w-sm">
            <CardHeader>
                <CardTitle className="text-lg md:text-xl">
                    Account Details
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                    We just need to know a few more things so we can finish
                    setting up your account.
                </CardDescription>
            </CardHeader>

            <CardContent className="grid gap-6">
                <div className="grid gap-4">
                    {/* Name Fields */}
                    <div className="space-y-2" data-testid="name-fields">
                        <Label className="text-sm font-medium">
                            What's your name?
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Input
                                    placeholder="First name"
                                    value={firstName}
                                    onChange={(e) =>
                                        setFirstName(e.target.value)
                                    }
                                    className={
                                        errors.firstName ? 'border-red-500' : ''
                                    }
                                />
                            </div>
                            <div>
                                <Input
                                    placeholder="Last name"
                                    value={lastName}
                                    onChange={(e) =>
                                        setLastName(e.target.value)
                                    }
                                    className={
                                        errors.lastName ? 'border-red-500' : ''
                                    }
                                />
                            </div>
                        </div>
                        {(errors.firstName || errors.lastName) && (
                            <p className="text-sm text-red-500">
                                Please enter your name.
                            </p>
                        )}
                    </div>

                    {/* Phone Number */}
                    <div className="space-y-2" data-testid="phone-number-input">
                        <Label htmlFor="phone">What's your phone number?</Label>
                        <Input
                            id="phone"
                            type="tel"
                            placeholder="Phone number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                    </div>

                    {/* Country */}
                    <div className="space-y-2" data-testid="country-selector">
                        <Label>Which country are you from?</Label>
                        <Select
                            value={countryCode}
                            onValueChange={setCountryCode}
                        >
                            <SelectTrigger
                                className={
                                    errors.countryCode ? 'border-red-500' : ''
                                }
                            >
                                <SelectValue placeholder="Select your country" />
                            </SelectTrigger>
                            <SelectContent>
                                {countries.map((country) => (
                                    <SelectItem
                                        key={country.code}
                                        value={country.code}
                                    >
                                        {country.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.countryCode && (
                            <p className="text-sm text-red-500">
                                Please select your country.
                            </p>
                        )}
                    </div>

                    {/* Industry */}
                    <div className="space-y-2" data-testid="industry-selector">
                        <Label>Industry</Label>
                        <Select value={industry} onValueChange={setIndustry}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select your industry" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="architecture">
                                    Architecture
                                </SelectItem>
                                <SelectItem value="engineering">
                                    Engineering
                                </SelectItem>
                                <SelectItem value="marketing">
                                    Marketing
                                </SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                        {industry === 'other' && (
                            <Input
                                placeholder="Please specify your industry"
                                value={otherIndustry}
                                onChange={(e) =>
                                    setOtherIndustry(e.target.value)
                                }
                                className="mt-2"
                            />
                        )}
                    </div>

                    {/* Company Size */}
                    <div
                        className="space-y-2"
                        data-testid="company-size-selector"
                    >
                        <Label>Company Size</Label>
                        <Select
                            value={companySize}
                            onValueChange={setCompanySize}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select company size" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="just-me">Just me</SelectItem>
                                <SelectItem value="1-5">2-5 staff</SelectItem>
                                <SelectItem value="6-10">6-10 staff</SelectItem>
                                <SelectItem value="11-25">
                                    11-25 staff
                                </SelectItem>
                                <SelectItem value="26-50">
                                    26-50 staff
                                </SelectItem>
                                <SelectItem value="51-100">
                                    51-100 staff
                                </SelectItem>
                                <SelectItem value="101-200">
                                    101-200 staff
                                </SelectItem>
                                <SelectItem value="201-500">
                                    201-500 staff
                                </SelectItem>
                                <SelectItem value="500+">500+ staff</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Referral Source */}
                    <div
                        className="space-y-2"
                        data-testid="referral-source-selector"
                    >
                        <Label>How did you hear about us?</Label>
                        <Select
                            value={referralSource}
                            onValueChange={setReferralSource}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="google_search">
                                    Google/Search Engine
                                </SelectItem>
                                <SelectItem value="social_media">
                                    Social Media (LinkedIn, Facebook, Twitter,
                                    etc.)
                                </SelectItem>
                                <SelectItem value="online_ads">
                                    Online Ads
                                </SelectItem>
                                <SelectItem value="word_of_mouth">
                                    Word of Mouth/Referral from a Friend
                                </SelectItem>
                                <SelectItem value="review_site">
                                    Review Site (e.g., G2, Capterra)
                                </SelectItem>
                                <SelectItem value="blog_article">
                                    Blog/Article
                                </SelectItem>
                                <SelectItem value="email_newsletter">
                                    Email Newsletter
                                </SelectItem>
                                <SelectItem value="event_webinar">
                                    Event/Webinar
                                </SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                        {referralSource === 'other' && (
                            <Input
                                placeholder="Please specify"
                                value={otherReferralSource}
                                onChange={(e) =>
                                    setOtherReferralSource(e.target.value)
                                }
                                className="mt-2"
                            />
                        )}
                    </div>

                    {/* Terms Acceptance */}
                    <div className="flex items-start space-x-2">
                        <input
                            type="checkbox"
                            id="termsAccepted"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            disabled={isSubmitting}
                            className="mt-1"
                        />
                        <Label
                            htmlFor="termsAccepted"
                            className="text-sm font-normal cursor-pointer"
                        >
                            I agree to the{' '}
                            <a
                                href="/terms"
                                className="underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Coincraft Terms of Service
                            </a>
                        </Label>
                    </div>
                    {errors.termsAccepted && (
                        <p className="text-sm text-red-500">
                            You must accept the Terms of Service to continue.
                        </p>
                    )}
                </div>

                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full"
                    size="lg"
                >
                    {isSubmitting ? 'Saving...' : 'Start Using Coincraft!'}
                </Button>
            </CardContent>
        </Card>
    )
}

const MagicLinkVerify = ({ redirectPath = '/' }: { redirectPath?: string }) => {
    const [, setLocation] = useLocation()
    const [status, setStatus] = useState<'verifying' | 'success' | 'error'>(
        'verifying'
    )
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search)
        const token = searchParams.get('token')

        if (!token) {
            setErrorMessage('No verification token provided.')
            setStatus('error')
            return
        }

        const verifyToken = async () => {
            try {
                const serverUrl =
                    process.env.REACT_APP_NODE_SERVER_URL ||
                    'http://localhost:8080'
                const response = await fetch(
                    `${serverUrl}/api/auth/magic-link/verify?token=${token}`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        redirect: 'manual',
                    }
                )

                if (response.type === 'opaqueredirect') {
                    // Check session to determine if verification succeeded
                    const sessionResponse = await fetch(
                        `${serverUrl}/api/auth/get-session`,
                        {
                            credentials: 'include',
                        }
                    )
                    const sessionData = await sessionResponse.json()

                    if (sessionData.user) {
                        setStatus('success')
                        setTimeout(() => {
                            window.location.href = redirectPath
                        }, 1000)
                    } else {
                        setErrorMessage('Email link is invalid or expired.')
                        setStatus('error')
                    }
                    return
                }

                if (response.status === 200) {
                    setStatus('success')
                    setTimeout(() => {
                        window.location.href = redirectPath
                    }, 1000)
                    return
                }

                try {
                    const text = await response.text()
                    const result = JSON.parse(text)
                    setErrorMessage(
                        result.error?.message ||
                            result.message ||
                            'Email link is invalid or expired.'
                    )
                } catch {
                    setErrorMessage('Email link is invalid or expired.')
                }
                setStatus('error')
            } catch (err) {
                setErrorMessage(
                    'Unable to verify the email link. Please try again.'
                )
                setStatus('error')
            }
        }

        verifyToken()
    }, [redirectPath])

    return (
        <Card className="w-full max-w-md mb-28">
            <CardHeader>
                <CardTitle className="text-lg md:text-xl flex items-center gap-3">
                    {status === 'verifying' && 'Verifying Email link'}
                    {status === 'success' && (
                        <>
                            <svg
                                className="h-6 w-6 text-green-600 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            <span>Welcome Back!</span>
                        </>
                    )}
                    {status === 'error' && (
                        <>
                            <svg
                                className="h-6 w-6 text-red-600 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                            <span>Verification Failed</span>
                        </>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-2 pb-6">
                {status === 'verifying' && (
                    <div className="space-y-4 flex flex-col items-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
                        <p className="text-center text-muted-foreground text-sm">
                            Please wait while we verify your email link...
                        </p>
                    </div>
                )}
                {status === 'success' && (
                    <div className="space-y-2 animate-in fade-in duration-500">
                        <p className="text-base">Successfully verified!</p>
                        <p className="text-sm text-muted-foreground">
                            Redirecting you now...
                        </p>
                    </div>
                )}
                {status === 'error' && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <div className="space-y-3">
                            <p className="text-base text-foreground">
                                {errorMessage}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                This link may have expired or already been used.
                            </p>
                        </div>
                        <div className="space-y-3">
                            <Button
                                onClick={() => setLocation('/auth/magic-link')}
                                className="w-full"
                            >
                                Request New Email link
                            </Button>
                            <Button
                                onClick={() => setLocation('/auth/sign-in')}
                                variant="outline"
                                className="w-full"
                            >
                                Sign In with Password
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

const SignOutView = () => {
    const [, setLocation] = useLocation()

    useEffect(() => {
        const performSignOut = async () => {
            try {
                await SessionStore.logout()
            } catch (error) {
                console.error('Sign out error:', error)
                // Even if logout fails, redirect to sign-in
                setLocation('/auth/sign-in')
            }
        }

        performSignOut()
    }, [setLocation])

    return (
        <Card className="w-full max-w-md">
            <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
                <p className="text-center text-muted-foreground">
                    Signing out...
                </p>
            </CardContent>
        </Card>
    )
}
